// File service for handling uploads and downloads
import { API_BASE_URL } from './api';

export interface UploadUrlResponse {
  upload_url: string;
  file_name: string;
  expiration: number;
  content_type?: string;
}

export interface PresignedUrlResponse {
  presigned_url: string;
  file_name: string;
  expiration: number;
  operation: string;
}

class FileService {
  private baseUrl = `${API_BASE_URL}/api`;

  /**
   * Generate a presigned URL for uploading a file
   */
  async getUploadUrl(fileName: string, contentType?: string): Promise<UploadUrlResponse> {
    const token = localStorage.getItem('access_token');
    console.log('Token check for upload:', token ? 'Token exists' : 'No token found');
    if (!token) {
      throw new Error('Authentication required. Please log in to upload files.');
    }

    const params = new URLSearchParams({
      file_name: fileName,
      ...(contentType && { content_type: contentType })
    });

    const response = await fetch(`${this.baseUrl}/files/upload-url?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to get upload URL: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Upload a file using the presigned URL with timeout and retry
   */
  async uploadFile(file: File, uploadUrl: string, contentType?: string, timeoutMs: number = 120000): Promise<void> {
    const headers: Record<string, string> = {};
    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers,
        body: file,
        mode: 'cors',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Upload timeout after ${timeoutMs / 1000} seconds`);
      }
      throw error;
    }
  }

  /**
   * Upload file through backend proxy (alternative method to avoid CORS)
   */
  async uploadFileViaBackend(file: File, fileName?: string): Promise<string> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const finalFileName = fileName || this.generateFileName(file);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_name', finalFileName);

    const response = await fetch(`${this.baseUrl}/files/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to upload file: ${response.status}`);
    }

    const data = await response.json();
    return data.file_url || data.url;
  }

  /**
   * Complete file upload process: get upload URL and upload file
   */
  async uploadFileComplete(file: File, fileName?: string): Promise<string> {
    // Generate a unique filename if not provided
    const finalFileName = fileName || this.generateFileName(file);
    
    // Get upload URL
    const uploadResponse = await this.getUploadUrl(finalFileName, file.type);
    
    // Try to upload the file
    await this.uploadFile(file, uploadResponse.upload_url, file.type);
    
    // After successful upload, get a proper download URL
    try {
      const downloadUrl = await this.getDownloadUrl(finalFileName);
      return downloadUrl;
    } catch (error) {
      console.warn('Failed to get download URL, using direct URL:', error);
      // Fallback: return the file URL (remove query parameters from upload URL)
      const fileUrl = uploadResponse.upload_url.split('?')[0];
      return fileUrl;
    }
  }

  /**
   * Generate a unique filename for uploaded files
   */
  private generateFileName(file: File): string {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 15);
    const extension = file.name.split('.').pop() || '';
    const baseName = file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]/g, '_');
    
    return `${timestamp}_${randomId}_${baseName}.${extension}`;
  }

  /**
   * Get a download URL for a file
   */
  async getDownloadUrl(fileName: string): Promise<string> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const params = new URLSearchParams({
      file_name: fileName
    });

    const response = await fetch(`${this.baseUrl}/files/download-url?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to get download URL: ${response.status}`);
    }

    const data = await response.json();
    return data.download_url;
  }

  /**
   * Validate file type for uploads
   */
  isValidFileType(file: File): boolean {
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov'];
    
    return validImageTypes.includes(file.type) || validVideoTypes.includes(file.type);
  }

  /**
   * Get file type category (image or video)
   */
  getFileTypeCategory(file: File): 'image' | 'video' | 'unknown' {
    if (file.type.startsWith('image/')) {
      return 'image';
    } else if (file.type.startsWith('video/')) {
      return 'video';
    }
    return 'unknown';
  }

  /**
   * Upload file and automatically add as companion asset with retry logic
   */
  async uploadAndAddAsset(
    file: File, 
    companionId: string, 
    assetType: 'image' | 'video' = 'image',
    isPrimary: boolean = false,
    maxRetries: number = 2
  ): Promise<{
    status: string;
    message: string;
    file_name: string;
    download_url: string;
    companion_id?: string;
    is_primary?: boolean;
  }> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams({
      companion_id: companionId,
      asset_type: assetType,
      is_primary: isPrimary.toString(),
      folder: ''
    });

    // Calculate timeout based on file size (minimum 30s, +10s per MB)
    const fileSizeMB = file.size / (1024 * 1024);
    const timeoutMs = Math.max(30000, 30000 + (fileSizeMB * 10000)); // 30s + 10s per MB

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        console.log(`Upload attempt ${attempt}/${maxRetries + 1} for ${file.name} (${fileSizeMB.toFixed(2)}MB, timeout: ${timeoutMs/1000}s)`);

        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
          // Try the full upload-and-add-asset endpoint first
          const response = await fetch(`${this.baseUrl}/files/upload-and-add-asset?${params}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            console.log(`✅ Upload successful on attempt ${attempt}`);
            return response.json();
          }

          // If not the last attempt, try again
          if (attempt <= maxRetries) {
            console.warn(`Upload attempt ${attempt} failed with status ${response.status}, retrying...`);
            await this.delay(2000 * attempt); // Exponential backoff
            continue;
          }

          // Last attempt failed, try fallback
          console.warn('All main upload attempts failed, trying simple upload fallback...');
          const errorData = await response.json().catch(() => ({}));
          console.warn('Upload error:', errorData);

        } catch (fetchError) {
          clearTimeout(timeoutId);
          
          if (fetchError instanceof Error && fetchError.name === 'AbortError') {
            if (attempt <= maxRetries) {
              console.warn(`Upload attempt ${attempt} timed out after ${timeoutMs/1000}s, retrying...`);
              await this.delay(3000 * attempt);
              continue;
            }
            throw new Error(`Upload timeout after ${timeoutMs/1000} seconds (tried ${maxRetries + 1} times)`);
          }
          
          if (attempt <= maxRetries) {
            console.warn(`Upload attempt ${attempt} failed:`, fetchError, 'retrying...');
            await this.delay(2000 * attempt);
            continue;
          }
          
          throw fetchError;
        }

        // Fallback to simple upload (only on last attempt)
        console.log('Trying simple upload fallback...');
        const simpleFormData = new FormData();
        simpleFormData.append('file', file);
        
        const simpleParams = new URLSearchParams({
          folder: ''
        });

        const simpleController = new AbortController();
        const simpleTimeoutId = setTimeout(() => simpleController.abort(), timeoutMs);

        try {
          const simpleResponse = await fetch(`${this.baseUrl}/files/upload-simple?${simpleParams}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: simpleFormData,
            signal: simpleController.signal,
          });

          clearTimeout(simpleTimeoutId);

          if (!simpleResponse.ok) {
            const simpleErrorData = await simpleResponse.json().catch(() => ({}));
            throw new Error(simpleErrorData.detail || `Simple upload failed: ${simpleResponse.status}`);
          }

          const simpleResult = await simpleResponse.json();
          
          // Return in the expected format
          return {
            status: simpleResult.status,
            message: `${simpleResult.message} (Fallback upload - you may need to save manually)`,
            file_name: simpleResult.file_name,
            download_url: simpleResult.download_url,
            companion_id: companionId,
            is_primary: isPrimary
          };

        } catch (simpleError) {
          clearTimeout(simpleTimeoutId);
          throw new Error(`All upload methods failed. Last error: ${simpleError instanceof Error ? simpleError.message : 'Unknown error'}`);
        }

      } catch (error) {
        if (attempt <= maxRetries) {
          console.warn(`Upload attempt ${attempt} failed, retrying in ${2000 * attempt}ms...`, error);
          await this.delay(2000 * attempt);
          continue;
        }
        
        console.error('All upload attempts failed:', error);
        throw error;
      }
    }

    throw new Error('Upload failed after all retry attempts');
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const fileService = new FileService();